import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Select, MenuItem, FormControl, TablePagination,
  Popover,
} from '@material-ui/core';

import { useTranslation } from 'react-i18next';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { format } from 'date-fns';
import { WarningTriangleIconSmall } from '../icons';


function IncidenciasTab({ data, colors, portals = [], locale = 'es' }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Default: 1st of current month → yesterday (last day with data)
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      startDate: new Date(yesterday.getFullYear(), yesterday.getMonth(), 1),
      endDate: yesterday,
      key: 'selection',
    };
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarAnchor = useRef(null);

  const [filterPortal, setFilterPortal] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const filteredDetails = useMemo(() => {
    let rows = data.details || [];

    if (dateRange.startDate && dateRange.endDate) {
      const start = dateRange.startDate.toISOString().substring(0, 10);
      const end = dateRange.endDate.toISOString().substring(0, 10);
      rows = rows.filter(r => {
        const dateStr = (r.start || '').substring(0, 10);
        return dateStr >= start && dateStr <= end;
      });
    }

    if (filterPortal !== 'all') {
      const activePortal = portals.find(p => p.key === filterPortal);
      if (activePortal?.pattern) {
        rows = rows.filter(r => activePortal.pattern.test(r.objective));
      }
    }

    if (filterService !== 'all') {
      rows = rows.filter(r => r.objective === filterService);
    }

    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter(row =>
        row.id.toString().includes(s) ||
        row.objective.toLowerCase().includes(s) ||
        row.incident.toLowerCase().includes(s) ||
        row.duration.toLowerCase().includes(s)
      );
    }

    return rows;
  }, [data.details, search, dateRange, filterPortal, filterService]);

  const objectiveOptions = useMemo(() => {
    let rows = data.details || [];

    if (dateRange.startDate && dateRange.endDate) {
      const start = dateRange.startDate.toISOString().substring(0, 10);
      const end = dateRange.endDate.toISOString().substring(0, 10);
      rows = rows.filter(r => {
        const dateStr = (r.start || '').substring(0, 10);
        return dateStr >= start && dateStr <= end;
      });
    }

    if (filterPortal !== 'all') {
      const activePortal = portals.find(p => p.key === filterPortal);
      if (activePortal?.pattern) {
        rows = rows.filter(r => activePortal.pattern.test(r.objective));
      }
    }

    return [...new Set(rows.map(r => r.objective).filter(Boolean))].sort();
  }, [data.details, dateRange, filterPortal, portals]);

  useMemo(() => {
    if (filterService !== 'all' && !objectiveOptions.includes(filterService)) {
      setFilterService('all');
    }
  }, [objectiveOptions]);

  useMemo(() => setPage(0), [dateRange, filterPortal, filterService, search]);

  const filteredSummary = useMemo(() => {
    const count = filteredDetails.length;
    const totalSeg = filteredDetails.reduce((s, d) => s + (d._duracionSeg || 0), 0);
    const avgSeg = count ? Math.round(totalSeg / count) : 0;
    const m = Math.floor(avgSeg / 60);
    const sec = avgSeg % 60;
    const avgDuration = m > 0 && sec > 0 ? `${m}m ${sec}s` : m > 0 ? `${m}m` : `${sec}s`;
    const uniqueDays = new Set(filteredDetails.map(d => (d.start || '').substring(0, 10)).filter(Boolean));
    return { count, avgDuration, daysAffected: uniqueDays.size };
  }, [filteredDetails]);

  const summaryCards = [
    { label: t('ind.incidents.count'), value: filteredSummary.count, flex: 253 },
    { label: t('ind.incidents.avg-duration'), value: filteredSummary.avgDuration, flex: 395 },
    { label: t('ind.incidents.days-affected'), value: filteredSummary.daysAffected, flex: 243 },
  ];

  const handleDateChange = (period) => {
    if (period && period.statePeriod) {
      setDateRange(period.statePeriod);
    }
  };

  const handleMonthNavigate = useCallback((newDate) => {
    const start = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (start > yesterday) return;
    const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
    const end = lastDay > yesterday ? yesterday : lastDay;
    setDateRange({ startDate: start, endDate: end, key: 'selection' });
    setCalendarOpen(false);
  }, []);



  const dateLabel = `${format(dateRange.startDate, 'dd/MM/yyyy')} - ${format(dateRange.endDate, 'dd/MM/yyyy')}`;

  const periodLabel = useMemo(() => {
    const start = dateRange.startDate;
    const end = dateRange.endDate;
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      const name = start.toLocaleDateString(locale, { month: 'long' });
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
    const fmt = (d) => d.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`;
  }, [dateRange, locale]);

  return (
    <Box>
      <Box display="flex" style={{ gap: 24, marginBottom: 24, justifyContent: 'space-between' }}>
        {summaryCards.map((card, idx) => (
          <Box
            key={idx}
            style={{
              flex: `0 0 ${Math.round((card.flex / 1312) * 100)}%`,
              height: 80,
              backgroundColor: '#fff',
              borderRadius: 12,
              padding: '12px 24px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" style={{ color: '#888', fontSize: '0.7rem' }}>{periodLabel}</Typography>
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
              <Typography style={{ fontWeight: 700, fontSize: '0.95rem', color: colors.textPrimary }}>
                {card.label}:
              </Typography>
              <Typography style={{ fontWeight: 700, color: colors.incidentNumber, fontSize: '1.8rem', lineHeight: 1 }}>
                {card.value}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Title */}
      <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 16 }}>
        <Box style={{
          width: 32, height: 32, borderRadius: 8, backgroundColor: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <WarningTriangleIconSmall/>
        </Box>
        <Typography style={{
          fontWeight: 600, fontSize: '1rem', color: colors.textPrimary,
        }}>
          {t('ind.incidents.detail')}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" mb={2} style={{ gap: 16 }}>
          <TextField
            size="small"
            variant="standard"
            placeholder={t('ind.incidents.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260, backgroundColor: '#fff', borderRadius: 8 }}
            InputProps={{ disableUnderline: true, style: { backgroundColor: '#fff', padding: '6px 12px', borderRadius: 8 } }}
          />
          <Typography style={{ fontSize: '0.85rem', color: colors.textPrimary, fontWeight: 600 }}>
            Filtros:
          </Typography>
          {/* Calendar date range filter */}
          <Box
            ref={calendarAnchor}
            onClick={() => setCalendarOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              // border: '1px solid #ccc',
              borderRadius: 8,
              padding: '6px 14px',
              cursor: 'pointer',
              backgroundColor: '#fff'
            }}
          >
            <Typography style={{ fontSize: '0.85rem', color: '#444' }}>
              {dateLabel}
            </Typography>
            <span style={{ fontSize: 10, color: '#888' }}>▾</span>
          </Box>
          <Popover
            open={calendarOpen}
            anchorEl={calendarAnchor.current}
            onClose={() => setCalendarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            style={{ zIndex: 10000 }}
            PaperProps={{
              style: {
                borderRadius: 13,
                boxShadow: '0px 4px 20px rgba(0,0,0,0.08), 0px 1px 4px rgba(0,0,0,0.04)',
                padding: 8,
              },
            }}
          >
            <DateRangePicker
              key={`${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`}
              changePeriodFn={handleDateChange}
              dateRangeFromParent={dateRange}
              showMonthAndYearPickers
              showDateDisplay={false}
              showMonthArrow
              onShownDateChange={handleMonthNavigate}
            />
          </Popover>
          <FormControl size="small" style={{ backgroundColor: '#fff', borderRadius: 8, padding: '4px 8px', display: 'flex', justifyContent: 'center' }}>
            <Select disableUnderline value={filterPortal} onChange={(e) => setFilterPortal(e.target.value)} style={{ minWidth: 110, backgroundColor: '#fff', color: '#333', fontSize: '0.85rem' }} MenuProps={{ style: { zIndex: 10000 } }}>
              <MenuItem value="all">{t('ind.incidents.filter.all')}</MenuItem>
              {portals.map(p => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" style={{ backgroundColor: '#fff', borderRadius: 8, padding: '4px 8px', display: 'flex', justifyContent: 'center' }}>
            <Select disableUnderline value={filterService} onChange={(e) => setFilterService(e.target.value)} style={{ minWidth: 160, backgroundColor: '#fff', color: '#333', fontSize: '0.85rem' }} MenuProps={{ style: { zIndex: 10000 } }}>
              <MenuItem value="all">{t('ind.incidents.filter.all')}</MenuItem>
              {objectiveOptions.map(name => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>

        </Box>

        {/* Table card */}
        <Box style={{
          borderRadius: 12,
          padding: 24,
          backgroundColor: '#fff',
        }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 700 }}>{t('ind.incidents.col.id')}</TableCell>
              <TableCell style={{ fontWeight: 700 }}>{t('ind.incidents.col.objective')}</TableCell>
              <TableCell style={{ fontWeight: 700 }}>{t('ind.incidents.col.incident')}</TableCell>
              <TableCell style={{ fontWeight: 700 }}>{t('ind.incidents.col.start')}</TableCell>
              <TableCell style={{ fontWeight: 700 }}>{t('ind.incidents.col.end')}</TableCell>
              <TableCell style={{ fontWeight: 700 }}>{t('ind.incidents.col.duration')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDetails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" style={{ padding: '32px 0', color: '#888' }}>
                  {t('ind.incidents.empty')}
                </TableCell>
              </TableRow>
            ) : (
              filteredDetails
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, idx) => (
                <TableRow key={idx} style={{
                  height: 56,
                  borderBottom: '1px solid #E7E7E9',
                }}>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.objective}</TableCell>
                  <TableCell>{row.incident}</TableCell>
                  <TableCell>{row.start}</TableCell>
                  <TableCell>{row.end}</TableCell>
                  <TableCell>{row.duration}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {filteredDetails.length > rowsPerPage && (
          <TablePagination
            component="div"
            count={filteredDetails.length}
            page={page}
            onChangePage={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[20]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        )}
      </Box>
    </Box>
  );
}

export default IncidenciasTab;
